import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Check, AlertCircle, FileText, X } from 'lucide-react';
import { CONSENT_CONFIG } from '../services/consentService';

interface ConsentModalProps {
  isOpen: boolean;
  onAccept: (ageVerified: boolean, termsAccepted: boolean) => void;
  onDecline: () => void;
  isUpdate?: boolean; // Whether this is a policy update for existing users
  userEmail?: string; // For personalized messaging
}

const ConsentModal: React.FC<ConsentModalProps> = ({
  isOpen,
  onAccept,
  onDecline,
  isUpdate = false,
  userEmail
}) => {
  const [ageVerified, setAgeVerified] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}>
      <motion.div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            {isUpdate ? (
              <AlertCircle className="h-6 w-6 text-orange-600 mr-2" />
            ) : (
              <FileText className="h-6 w-6 text-blue-600 mr-2" />
            )}
            <h2 className="text-xl font-semibold text-gray-800">
              {isUpdate ? 'Updated Terms & Privacy Policy' : 'Terms of Service & Privacy Policy'}
            </h2>
          </div>
          <button
            onClick={onDecline}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
        {isUpdate ? (
          <div>
            <p className="text-gray-600 mb-4">
              We have updated our Terms of Service and Privacy Policy. 
              To continue using the app, please review and accept our updated terms.
            </p>
            {userEmail && (
              <p className="text-sm text-gray-500 mb-4">
                This update affects your account: <span className="font-medium">{userEmail}</span>
              </p>
            )}
          </div>
        ) : (
          <div>
            <p className="text-gray-600">
              To create an account and use our cruise tracking app, you must agree to our Terms of Service and Privacy Policy. 
              This includes:
            </p>
          </div>
        )}
        
        <ul className="text-sm text-gray-600 space-y-2">
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span>Data collection and storage for cruise tracking</span>
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span>Third-party services that may use cookies or tracking</span>
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span>Local storage for your preferences and data</span>
          </li>
          <li className="flex items-start">
            <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span>User authentication and account management</span>
          </li>
        </ul>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Agreement is required</strong> to use this application. 
            {isUpdate ? ' You must accept the updated terms to continue.' : ' Without agreement, you cannot create an account or use the app.'}
          </p>
        </div>
        
        {/* Age Verification and Terms Acceptance */}
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="ageVerification"
              checked={ageVerified}
              onChange={(e) => setAgeVerified(e.target.checked)}
              className="mt-1 h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
            />
            <label htmlFor="ageVerification" className="text-sm text-gray-700">
              I confirm that I am 18 years of age or older, or I am between 13-17 years old and have parental consent to use this app.
            </label>
          </div>
          
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="termsAcceptance"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
            />
            <label htmlFor="termsAcceptance" className="text-sm text-gray-700">
              I have read and agree to the{' '}
              <a 
                href="https://www.cruisingalong.com/terms-of-service.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-500 underline"
              >
                App Terms of Service
              </a>
              {' '}and{' '}
              <a 
                href="https://www.cruisingalong.com/privacy-policy.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-500 underline"
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>
        </div>
        
        <div className="text-xs text-gray-400 text-center">
          Policy Version: {CONSENT_CONFIG.currentVersion}
        </div>
        
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onDecline}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
          >
            Decline
          </button>
          <button
            onClick={() => onAccept(ageVerified, termsAccepted)}
            disabled={!ageVerified || !termsAccepted}
            className={`flex-1 px-4 py-2 rounded-md transition-colors font-medium ${
              ageVerified && termsAccepted
                ? 'bg-secondary-600 text-white hover:bg-secondary-500'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Accept
          </button>
        </div>
        </div>
      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ConsentModal;
